import React, { useState } from 'react';
import {
  HelpCircle,
  Lightbulb,
  ShieldCheck,
  HelpCircle as FaqIcon,
  FileText,
  Smartphone,
  Monitor,
  Info,
  X
} from 'lucide-react';
import { AwarenessContentRow } from '../../../services/Writer/awarenessService';

interface LivePreviewProps {
  title: string;
  theme: string;
  shortDescription: string;
  sections: AwarenessContentRow[];
  eventType: string;
  startDate: string;
  endDate: string;
  region: string;
  tags: string;
  imagePreviewUrl: string;
  onClose?: () => void;
}

const renderFormattedText = (text: string, isDark: boolean = false) => {
  if (!text) return '';
  const lines = text.split('\n').map(line => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('- ')) {
      return line.replace('- ', '• ');
    } else if (trimmed.startsWith('* ')) {
      return line.replace('* ', '• ');
    }
    return line;
  });
  const processedText = lines.join('\n');
  const parts = processedText.split(/(\*[^*]+\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      const content = part.slice(1, -1);
      return (
        <strong key={index} className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {content}
        </strong>
      );
    }
    return part;
  });
};

const titleForSectionKey = (key: string) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === 'what') return 'What is this?';
  if (k === 'why') return 'Why It Matters';
  if (k === 'prevention') return 'Prevention & Care';
  if (k === 'faq') return 'Frequently Asked Questions';
  if (k === 'summary') return 'Summary & Takeaways';
  return key || 'Details';
};

const iconForSection = (key: string) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === 'what') return <HelpCircle className="w-5 h-5 text-blue-600" />;
  if (k === 'why') return <Lightbulb className="w-5 h-5 text-blue-600" />;
  if (k === 'prevention') return <ShieldCheck className="w-5 h-5 text-blue-600" />;
  if (k === 'faq') return <FaqIcon className="w-5 h-5 text-blue-600" />;
  return <FileText className="w-5 h-5 text-blue-600" />;
};

export const LivePreview: React.FC<LivePreviewProps> = ({
  title,
  theme,
  shortDescription,
  sections,
  eventType,
  startDate,
  endDate,
  region,
  tags,
  imagePreviewUrl,
  onClose,
}) => {
  const [viewMode, setViewMode] = useState<'mobile' | 'web'>('mobile');

  const tagList = tags
    ? tags.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-lg h-[calc(100vh-140px)] sticky top-6 flex flex-col">
      {/* View Switcher Header */}
      <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Live Preview</span>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-800 rounded text-gray-500 hover:text-gray-700 transition"
              title="Close Preview"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex bg-gray-200 dark:bg-gray-800 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('mobile')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              viewMode === 'mobile'
                ? 'bg-white dark:bg-gray-700 text-gray-950 dark:text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            User App
          </button>
          <button
            type="button"
            onClick={() => setViewMode('web')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              viewMode === 'web'
                ? 'bg-white dark:bg-gray-700 text-gray-950 dark:text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <Monitor className="w-3.5 h-3.5" />
            Web View
          </button>
        </div>
      </div>

      {/* no-scrollbar styles inline */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-950 p-3 flex items-center justify-center">
        {viewMode === 'mobile' ? (
          /* --- HIGH FIDELITY PREMIUM SMARTPHONE (IPHONE STYLE) SHELL --- */
          <div className="relative w-[250px] h-[94%] max-h-[570px] bg-slate-900 rounded-[44px] shadow-2xl p-[6px] border-[5px] border-slate-300 dark:border-slate-800 ring-[2px] ring-slate-950 flex flex-col transition-all">
            {/* Left side Volume Up & Down Buttons */}
            <div className="absolute left-[-10px] top-[100px] w-[3px] h-9 bg-slate-400 dark:bg-slate-700 rounded-l-sm" />
            <div className="absolute left-[-10px] top-[148px] w-[3px] h-9 bg-slate-400 dark:bg-slate-700 rounded-l-sm" />
            {/* Right side Power Button */}
            <div className="absolute right-[-10px] top-[120px] w-[3px] h-14 bg-slate-400 dark:bg-slate-700 rounded-r-sm" />

            {/* Dynamic Island */}
            <div className="absolute top-[14px] left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-full z-30 flex items-center justify-center">
              <div className="absolute right-3.5 w-1.5 h-1.5 rounded-full bg-blue-950/40" />
            </div>

            {/* Inner Screen Container */}
            <div className="flex-1 w-full h-full bg-[#F8FAFC] rounded-[38px] overflow-hidden flex flex-col pt-5 text-gray-900 border border-slate-950/20 shadow-inner">
              {/* Header bar */}
              <div className="bg-white border-b border-gray-200/60 px-4 py-2 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full border border-gray-200 flex items-center justify-center bg-white shadow-sm">
                  <span className="text-blue-900 text-[9px] font-bold">←</span>
                </div>
                <span className="text-[10.5px] font-bold text-gray-900 truncate max-w-[120px]">
                  {title || 'Health Awareness'}
                </span>
              </div>

              {/* Mobile View Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 no-scrollbar">
                {/* Featured Image */}
                {imagePreviewUrl && (
                  <div className="w-full h-[120px] rounded-xl overflow-hidden bg-white border border-gray-200/50 shadow-sm">
                    <img src={imagePreviewUrl} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Header Title Card */}
                <div className="bg-white rounded-lg p-3.5 border border-gray-200/50 shadow-sm space-y-2">
                  <h1 className="text-sm font-extrabold text-gray-900 leading-snug">
                    {title || 'Untitled Awareness Campaign'}
                  </h1>
                  {theme && (
                    <div className="flex items-center gap-1 text-blue-900 font-semibold text-[10px]">
                      <span>✦</span>
                      <span>{theme}</span>
                    </div>
                  )}
                  {shortDescription && (
                    <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-line border-t border-gray-100 pt-2">
                      {renderFormattedText(shortDescription)}
                    </p>
                  )}
                </div>

                {/* Sections Cards */}
                {sections.map((section, idx) => {
                  if (!section.content.trim()) return null;
                  return (
                    <div key={idx} className="bg-white rounded-lg p-3.5 border border-gray-200/50 shadow-sm space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="p-0.5 bg-blue-50 rounded">
                          {iconForSection(section.section)}
                        </div>
                        <h2 className="text-[11px] font-bold text-gray-900">
                          {titleForSectionKey(section.section)}
                        </h2>
                      </div>
                      <div className="border-t border-gray-100 pt-2 text-[11px] text-gray-600 leading-relaxed whitespace-pre-line">
                        {renderFormattedText(section.content)}
                      </div>
                    </div>
                  );
                })}

                {/* Disclaimer */}
                <div className="bg-red-50/60 rounded-lg p-2.5 border border-red-250 text-[9.5px] text-red-950 leading-relaxed space-y-1">
                  <div className="flex items-center gap-1 font-bold text-red-800">
                    <Info className="w-3 h-3" />
                    <span>Medical Disclaimer</span>
                  </div>
                  <p>
                    The information provided here is for general awareness and educational purposes only and should not be considered medical advice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* --- HIGH FIDELITY PREMIUM LAPTOP SHELL --- */
          <div className="relative w-full max-w-[500px] h-[340px] shadow-2xl flex flex-col transition-all">
            {/* Screen Bezel Frame (Silver/Black) */}
            <div className="bg-slate-950 rounded-t-xl p-[8px] pb-0 border-t border-x border-slate-800 flex-1 flex flex-col overflow-hidden shadow-inner">
              {/* Top Camera dot */}
              <div className="flex justify-center mb-1 flex-none">
                <div className="w-2 h-2 rounded-full bg-slate-900 border border-slate-850 flex items-center justify-center">
                  <div className="w-0.5 h-0.5 rounded-full bg-blue-900/60" />
                </div>
              </div>
              {/* Web Content Inner Viewport */}
              <div className="bg-white rounded-t-md border border-black/20 flex-1 flex flex-col overflow-hidden text-gray-800">
                {/* Mock Web Nav bar */}
                <div className="border-b border-gray-150 px-3.5 py-2.5 flex items-center justify-between bg-slate-50 flex-none">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <span className="font-extrabold text-blue-900 text-xs tracking-tight">Vedika.Health</span>
                  <span className="text-[9px] text-gray-400 bg-slate-100 px-3 py-0.5 rounded border">localhost:3000/awareness</span>
                </div>

                {/* Content View */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3.5 no-scrollbar">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <button type="button" className="text-[10px] px-2 py-0.5 border rounded text-gray-500 font-bold bg-white shadow-sm">
                      ← Back
                    </button>
                    <span className="text-[9px] bg-blue-900 text-white font-extrabold px-2 py-0.5 rounded-full">
                      {eventType === 'health' ? 'HEALTH DAY' : 'SEASONAL'}
                    </span>
                  </div>

                  {imagePreviewUrl && (
                    <div className="flex justify-center bg-gray-50 rounded-lg p-1.5 border border-gray-100">
                      <img
                        src={imagePreviewUrl}
                        alt="Hero"
                        className="max-h-[160px] object-contain rounded-lg"
                      />
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1">
                    {theme && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border border-blue-200 text-blue-900">
                        {theme}
                      </span>
                    )}
                    {region && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border border-blue-200 text-blue-900">
                        {region}
                      </span>
                    )}
                  </div>

                  <h1 className="text-base font-black text-gray-900">{title || 'Glaucoma Awareness Day'}</h1>

                  {shortDescription && (
                    <p className="text-[11px] text-gray-700 leading-relaxed border-t border-gray-100 pt-2 whitespace-pre-line">
                      {renderFormattedText(shortDescription)}
                    </p>
                  )}

                  {/* Sections list */}
                  {sections.length > 0 && (
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                      {sections.map((section, idx) => {
                        if (!section.content.trim()) return null;
                        return (
                          <div key={idx} className="space-y-0.5">
                            <h3 className="text-[11px] font-extrabold text-blue-900">
                              {titleForSectionKey(section.section)}
                            </h3>
                            <p className="text-[10.5px] text-gray-700 leading-relaxed whitespace-pre-line">
                              {renderFormattedText(section.content)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Tags */}
                  {tagList.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {tagList.map((tag) => (
                        <span key={tag} className="text-[9px] bg-gray-105 text-blue-900 font-bold px-1.5 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Web Disclaimer */}
                  <div className="bg-red-50 border border-red-200/50 rounded-lg p-2.5 space-y-1">
                    <span className="text-[10px] font-bold text-red-700 block">Disclaimer</span>
                    <p className="text-[10px] text-gray-650 leading-relaxed">
                      The information provided here is for general awareness and educational purposes only and should not be considered as a medical advice; please consult a qualified healthcare professional for personalized medical guidance.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Metallic Keyboard deck Base */}
            <div className="h-4 bg-gradient-to-b from-slate-350 via-slate-200 to-slate-400 rounded-b-2xl border-t border-slate-500/80 relative shadow-inner">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-1.5 bg-slate-500/60 rounded-b-md" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
